import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import {
  AuthModule,
  GetMsalInterceptorConfiguration,
  LoginCallbackComponent,
  getB2CMsalGuardConfig,
  getB2CMsalInstance,
  getB2CMsalInterceptorConfig,
} from '@vinnova/auth';
import {
  chatbotApiUrl as CHATBOT_API_URL,
  ChatbotWidgetComponent,
} from '@vinnova/chatbot';
import { ApimInterceptor, ETjansterModule } from '@vinnova/e-tjanster';
import {
  FeatureService,
  FeatureTogglingModule,
  initializeFeatures,
} from '@vinnova/feature-toggling';
import { LoggingModule } from '@vinnova/logging';
import { BankAccountClient, OregLibModule } from '@vinnova/oreg-lib';
import { TranslationModule } from '@vinnova/translation';
import { ModalModule, ToastModule } from '@vinnova/ui';
import * as i18nIsoCountries from 'i18n-iso-countries';
import * as en from 'i18n-iso-countries/langs/en.json';
import * as sv from 'i18n-iso-countries/langs/sv.json';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutComponent } from './layout/layout.component';
import { PdfLoadingComponent } from './pdf-loading/pdf-loading.component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
i18nIsoCountries.registerLocale((sv as any).default);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
i18nIsoCountries.registerLocale(en as any);

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const interceptorConfig = getB2CMsalInterceptorConfig(
    environment.b2cMsalConfig,
    undefined,
    [
      {
        url: `${environment.etjansterCommonApiUrl}/account/loginb2c`,
        clientId: environment.b2cMsalConfig.clientId.etjansterCommonApiClientId,
      },
      {
        url: `${environment.etjansterCommonApiUrl}/betalningsuppgifter`,
        clientId: environment.b2cMsalConfig.clientId.etjansterCommonApiClientId,
      },
      {
        url: `${environment.oregApiUrl}/*`,
        clientId: environment.b2cMsalConfig.clientId.oregApiClientId,
      },
    ],
    [
      `${environment.ansokanApiUrl}/ansokningsomgang/*/publication`,
      environment.resourceStringUrl,
      environment.featureUrl,
    ],
  );
  return GetMsalInterceptorConfiguration(
    interceptorConfig,
    environment.b2cMsalConfig,
  );
}

@NgModule({
  declarations: [AppComponent, LayoutComponent, PdfLoadingComponent],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ETjansterModule.forRoot(environment),
    FeatureTogglingModule.forRoot(environment),
    AuthModule.forRoot(environment, 'ansokan'),
    TranslationModule.forRoot(environment),
    OregLibModule.forRoot(environment, {
      provide: BankAccountClient,
      useClass: BankAccountClient,
    }),
    LoggingModule.forRoot(environment),
    ToastModule,
    ChatbotWidgetComponent,
    MsalModule.forRoot(
      getB2CMsalInstance(environment.b2cMsalConfig),
      getB2CMsalGuardConfig(),
      undefined,
    ),
    LoginCallbackComponent,
    ModalModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApimInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => {
      const initializerFn = initializeFeatures(inject(FeatureService));
      return initializerFn();
    }),
    {
      provide: CHATBOT_API_URL,
      useValue: environment.chatbotApiUrl,
    },
  ],
})
export class AppModule {}
